import {FC, useContext, useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {
    Button, Modal,
    ModalBody as Body,
    ModalFooter as Footer,
    Form,
    ModalHeader as Header,
    ModalTitle as Title
} from "react-bootstrap";
import GroupedAction from "Entity/Collection/ListingSetting/GroupedAction";
import LoadingButton from "../../Button/LoadingButton";
import Api from "Vendor/Api";
import Collection from "Config/Collection";
import DynamicRenamingExportConfig, {DynamicRenamingExportConfigValue} from "Config/Export/DynamicRenamingExportConfig";
import TextType from "../../../Vendor/Components/Form/Type/TextType";
import Property from "../../../Entity/Property";
import useFileNameValidation from "Hooks/useFileNameValidation";
import { BannerContext } from "Vendor/Context/BannerContextProvider";

export interface _DynamicRenamingExportModalProps {
    collection?: Collection
    showActionModals: {[key: string]: boolean|Property}
    handleShowActionModal: (groupedAction?: GroupedAction, actionFromMain?:boolean) => void
    fromMain: boolean
    handleShowMainActionModal: () => void
    selected: number[]
    actionModalFormIsLoading: boolean
    handleActionModalOnResponse: () => void
    handleActionModalOnSuccess: () => void
    handleActionModalOnSubmit: () => void
}

const _DynamicRenamingExportModal: FC<_DynamicRenamingExportModalProps> = ({
    collection,
    showActionModals,
    handleShowActionModal,
    fromMain,
    handleShowMainActionModal,
    selected,
    actionModalFormIsLoading,
    handleActionModalOnResponse,
    handleActionModalOnSuccess,
    handleActionModalOnSubmit,
}) => {
    const {t} = useTranslation()
    const { setBanner } = useContext(BannerContext)
    const {
        fileName, setFileName, error, requiredError, handleFileNameChange, checkFileNameRequired, resetRequiredError,
    } = useFileNameValidation({
        errorMessage: t("app.groupedActionContent.errors.invalidFileName"),
        requiredMessage: t("app.groupedActionContent.errors.archiveNameRequired"),
    })
    const isOpen = showActionModals.dynamicRenamingExport !== false

    useEffect(() => {
        if (isOpen) {
            resetRequiredError()
        }
    }, [isOpen, resetRequiredError])
    const [exportConfig, setExportConfig] = useState<DynamicRenamingExportConfigValue>(DynamicRenamingExportConfig.Pim)

    const onSubmit = () => {
        if (!checkFileNameRequired() || error) return

        handleActionModalOnSubmit()
        Api.post('/grouped-action/dynamic-renaming-export', {
            listing: collection,
            selected: selected,
            fileName: fileName,
            exportConfig: exportConfig,
        }, {
            responseType: 'blob'
        })
            .then((response ) => {
                let disposition = response.headers['content-disposition'];
                let contentType = response.headers['content-type'];

                if("application/json" === contentType) {
                    setBanner({ type: 'error', i18nTitle: 'app.save', i18nMessage: t('app.listing_setting.grouped_action.download_exceeds_max_size_warning', { maxSize: '1Go' }), time: 5000 })

                    handleShowActionModal()
                } else {
                    let filename = disposition?.split(';')[1].split('filename')[1].split('=')[1].trim().replace(/"/g, '');
                    const href = URL.createObjectURL(response.data);

                    const link = document.createElement('a');
                    link.href = href;
                    link.setAttribute('download', filename);
                    document.body.appendChild(link);
                    link.click();

                    document.body.removeChild(link);
                    URL.revokeObjectURL(href);

                    handleActionModalOnSuccess()
                }
            })
            .catch(() => {
            })
            .then(() => {
                handleActionModalOnResponse()
                setFileName('')
                setExportConfig(DynamicRenamingExportConfig.Pim)
            })
    }

    return <Modal show={isOpen} onHide={handleShowActionModal}>
        <Header closeButton={!fromMain}>
            <div className="modal-block-header">
                {fromMain && <i className="fa-solid fa-angle-left" onClick={handleShowMainActionModal}></i>}
                <Title>{t(`app.groupedActionTitle.dynamicRenamingExport.${collection}`, {count: selected.length})}</Title>
            </div>
        </Header>
        <Body>
            <Form.Group className="mb-3">
                <Form.Label>{t('app.groupedActionContent.dynamicRenamingExport.exportConfigLabel')}</Form.Label>
                <Form.Select value={exportConfig} onChange={(event) => setExportConfig(event.target.value as DynamicRenamingExportConfigValue)}>
                    <option value={DynamicRenamingExportConfig.Pim}>{t('app.groupedActionContent.dynamicRenamingExport.exportConfigOptions.pim')}</option>
                    <option value={DynamicRenamingExportConfig.WebsiteUrl}>{t('app.groupedActionContent.dynamicRenamingExport.exportConfigOptions.websiteUrl')}</option>
                </Form.Select>
            </Form.Group>
            <TextType
                className={error || requiredError ? "is-invalid" : ""}
                required={true}
                fullName="fileName"
                id="fileName"
                name="fileName"
                value={fileName}
                onChange={handleFileNameChange}
                i18nLabel="app.groupedActionContent.download.inputLabel"
            />
            {requiredError && <div className="text-danger">{requiredError}</div>}
            {error && <div className="text-danger">{error}</div>}
        </Body>
        <Footer>
            {!fromMain && <Button variant="secondary" onClick={() => {
                handleShowActionModal()
            }}>
                {t('app.cancel')}
            </Button>}
            <LoadingButton variant="primary" isLoading={actionModalFormIsLoading} disabled={!!error} onClick={onSubmit}>
                {t('app.download')}
            </LoadingButton>
        </Footer>
    </Modal>
}

export default _DynamicRenamingExportModal
